import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = {
  name: "",
  category: "Filipino Grocery",
  price: 0,
  unit: "each",
  stock: 0,
  low_stock_threshold: 5,
  description: "",
  image: "",
  available: true,
  fresh_today: false,
  featured: false,
  tax_exempt: true,
  sku: "",
  supplier: "",
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then(({ data }) => setItems(data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/products/${editing.id}`, editing);
      else await api.post("/products", editing);

      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between mb-5">
        <h1 className="font-heading text-3xl text-[#FAF9F6]">Grocery Products</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-chow" data-testid="product-new">
          <Plus className="w-4 h-4 inline" /> New
        </button>
      </div>

      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#D4AF37]/80 text-xs uppercase border-b border-[#D4AF37]/15">
              <th className="p-3">Name</th>
              <th>Cat</th>
              <th>Price</th>
              <th>Stock</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="p-3 font-heading text-[#FAF9F6]">{p.name}</td>
                <td className="text-xs">{p.category}</td>
                <td className="text-[#D4AF37]">${p.price.toFixed(2)}/{p.unit}</td>
                <td className={p.stock <= p.low_stock_threshold ? "text-[#E8A87C]" : ""}>{p.stock}</td>
                <td>
                  <button onClick={() => setEditing(p)} className="text-[#D4AF37] mr-2">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(p.id)} className="text-[#E8A87C]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="admin-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-2xl text-[#D4AF37] mb-3">
              {editing.id ? "Edit" : "New"} product
            </h3>

            <div className="space-y-2 text-sm">
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Name"
                className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              />

              <input
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                placeholder="Category"
                className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              />

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value || 0) })}
                  placeholder="Price"
                  className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
                />

                <input
                  value={editing.unit}
                  onChange={(e) => setEditing({ ...editing, unit: e.target.value })}
                  placeholder="Unit (lb, each)"
                  className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={editing.stock}
                  onChange={(e) => setEditing({ ...editing, stock: parseInt(e.target.value || 0) })}
                  placeholder="Stock"
                  className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
                />

                <input
                  type="number"
                  value={editing.low_stock_threshold}
                  onChange={(e) => setEditing({ ...editing, low_stock_threshold: parseInt(e.target.value || 0) })}
                  placeholder="Low threshold"
                  className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
                />
              </div>

              <ImageUpload
                value={editing.image}
                onChange={(url) => setEditing({ ...editing, image: url })}
                recommendedSize="800 × 800"
                aspect="aspect-square"
                testId="product-image"
              />

              <div className="grid grid-cols-2 gap-2 text-[#FAF9F6] text-xs">
                <label className="flex gap-2 items-center">
                  <input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} />
                  Available
                </label>

                <label className="flex gap-2 items-center">
                  <input type="checkbox" checked={editing.fresh_today} onChange={(e) => setEditing({ ...editing, fresh_today: e.target.checked })} />
                  Fresh Today
                </label>

                <label className="flex gap-2 items-center">
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                  Featured
                </label>

                <label className="flex gap-2 items-center">
                  <input type="checkbox" checked={editing.tax_exempt} onChange={(e) => setEditing({ ...editing, tax_exempt: e.target.checked })} />
                  Tax exempt
                </label>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={save} className="btn-chow flex-1" data-testid="product-save">Save</button>
              <button onClick={() => setEditing(null)} className="btn-chow-outline flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
